import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  InputField,
  INPUT_FIELD_PREVIEW_STATE_LABELS,
  INPUT_FIELD_PREVIEW_STATES,
  INPUT_FIELD_VARIANT_LABELS,
  INPUT_FIELD_VARIANTS,
  type InputFieldTag,
  type InputFieldVariant,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './InputFieldShowcase.css'

const FORM_FIELD_ORDER: InputFieldVariant[] = [
  'text',
  'email',
  'phone',
  'password',
  'address',
  'number',
  'amount',
  'card-number',
  'date',
  'time',
  'verification',
  'verification-v2',
  'tags',
  'message',
]

const FORM_FULL_WIDTH_VARIANTS = new Set<InputFieldVariant>([
  'address',
  'message',
])

const FORM_SPAN_TWO_VARIANTS = new Set<InputFieldVariant>(['tags'])

const createEmptyFormValues = (): Record<InputFieldVariant, string> =>
  INPUT_FIELD_VARIANTS.reduce(
    (values, fieldVariant) => ({ ...values, [fieldVariant]: '' }),
    {} as Record<InputFieldVariant, string>,
  )

const InputFieldShowcase: React.FC = () => {
  const [variant, setVariant] = useState<InputFieldVariant>('text')
  const [formValues, setFormValues] = useState(createEmptyFormValues)
  const [formTags, setFormTags] = useState<InputFieldTag[]>([])
  const [disabled, setDisabled] = useState(false)
  const [helperText, setHelperText] = useState('')
  const [optional, setOptional] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const selectedFieldRef = useRef<HTMLDivElement>(null)
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const bodyScrollRef = useRef<HTMLDivElement>(null)
  const footerScrollRef = useRef<HTMLDivElement>(null)
  const bodyContentRef = useRef<HTMLDivElement>(null)
  const isSyncingScrollRef = useRef(false)
  const [matrixScrollWidth, setMatrixScrollWidth] = useState(0)

  const syncMatrixScroll = (
    source: HTMLDivElement,
    targets: Array<HTMLDivElement | null>,
  ): void => {
    if (isSyncingScrollRef.current) {
      return
    }

    isSyncingScrollRef.current = true
    targets.forEach((target) => {
      if (target && target !== source) {
        target.scrollLeft = source.scrollLeft
      }
    })
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false
    })
  }

  const handleBodyScroll = (): void => {
    const body = bodyScrollRef.current
    if (!body) {
      return
    }

    syncMatrixScroll(body, [headerScrollRef.current, footerScrollRef.current])
  }

  const handleHeaderScroll = (): void => {
    const header = headerScrollRef.current
    if (!header) {
      return
    }

    syncMatrixScroll(header, [bodyScrollRef.current, footerScrollRef.current])
  }

  const handleFooterScroll = (): void => {
    const footer = footerScrollRef.current
    if (!footer) {
      return
    }

    syncMatrixScroll(footer, [bodyScrollRef.current, headerScrollRef.current])
  }

  useEffect(() => {
    selectedFieldRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }, [variant])

  useEffect(() => {
    const content = bodyContentRef.current
    if (!content) {
      return
    }

    const updateScrollWidth = (): void => {
      const rows = content.querySelectorAll<HTMLElement>(
        '.input-field-showcase__matrix-row',
      )
      let maxWidth = content.scrollWidth

      rows.forEach((row) => {
        maxWidth = Math.max(maxWidth, row.scrollWidth)
      })

      setMatrixScrollWidth(maxWidth)
    }

    updateScrollWidth()

    const observer = new ResizeObserver(updateScrollWidth)
    observer.observe(content)
    content
      .querySelectorAll<HTMLElement>('.input-field-showcase__matrix-row')
      .forEach((row) => observer.observe(row))

    return () => observer.disconnect()
  }, [])

  const matrixStyle =
    matrixScrollWidth > 0
      ? ({
          '--matrix-scroll-width': `${matrixScrollWidth}px`,
        } as React.CSSProperties)
      : undefined

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'input-field-variant',
            label: 'Variant',
            control: (
              <select
                id="input-field-variant"
                className="ui-field-control"
                value={variant}
                onChange={(event) =>
                  setVariant(event.target.value as InputFieldVariant)
                }
              >
                {INPUT_FIELD_VARIANTS.map((option) => (
                  <option key={option} value={option}>
                    {INPUT_FIELD_VARIANT_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'input-field-helper',
            label: 'Helper text',
            control: (
              <input
                id="input-field-helper"
                className="ui-field-control"
                type="text"
                value={helperText}
                onChange={(event) => setHelperText(event.target.value)}
                placeholder={`Shown on ${INPUT_FIELD_VARIANT_LABELS[variant]}`}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'input-field-optional',
            label: 'Optional label',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="input-field-optional"
                  type="checkbox"
                  checked={optional}
                  onChange={(event) => setOptional(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'input-field-tooltip',
            label: 'Tooltip icon',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="input-field-tooltip"
                  type="checkbox"
                  checked={showTooltip}
                  onChange={(event) => setShowTooltip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'input-field-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="input-field-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [disabled, helperText, optional, showTooltip, variant],
  )

  const updateFormValue =
    (fieldVariant: InputFieldVariant) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): void => {
      setFormValues((currentValues) => ({
        ...currentValues,
        [fieldVariant]: event.target.value,
      }))
    }

  const clearFormValue = (fieldVariant: InputFieldVariant): void => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [fieldVariant]: '',
    }))
  }

  return (
    <>
      <UiPreviewCanvas
        title="Input field"
        description="Text inputs with prefixes, icons, tags, OTP cells, and helper text across default, focus, filled, disabled, error, optional, tooltip, and info states."
        stageClassName={`ui-preview__stage--wide input-field-showcase__stage-wrap`}
      >
        <div className={`input-field-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Interactive form with every input field variant. Use the variant
              selector to highlight a field and apply helper text.
            </p>
            <form
              className="input-field-showcase__form"
              onSubmit={(event) => event.preventDefault()}
            >
              {FORM_FIELD_ORDER.map((fieldVariant) => (
                <div
                  key={fieldVariant}
                  ref={variant === fieldVariant ? selectedFieldRef : undefined}
                  className={`input-field-showcase__form-field${
                    FORM_FULL_WIDTH_VARIANTS.has(fieldVariant)
                      ? ' input-field-showcase__form-field--full'
                      : ''
                  }${
                    FORM_SPAN_TWO_VARIANTS.has(fieldVariant)
                      ? ' input-field-showcase__form-field--span-2'
                      : ''
                  }${
                    variant === fieldVariant
                      ? ' input-field-showcase__form-field--selected'
                      : ''
                  }`}
                >
                  <InputField
                    variant={fieldVariant}
                    value={formValues[fieldVariant]}
                    fullWidth={FORM_FULL_WIDTH_VARIANTS.has(fieldVariant)}
                    rows={fieldVariant === 'message' ? 3 : undefined}
                    tags={fieldVariant === 'tags' ? formTags : undefined}
                    onTagsChange={
                      fieldVariant === 'tags' ? setFormTags : undefined
                    }
                    optional={optional}
                    showTooltip={showTooltip}
                    helperText={
                      variant === fieldVariant && helperText
                        ? helperText
                        : undefined
                    }
                    disabled={disabled}
                    onChange={updateFormValue(fieldVariant)}
                    onClear={() =>
                      fieldVariant === 'tags'
                        ? setFormTags([])
                        : clearFormValue(fieldVariant)
                    }
                  />
                </div>
              ))}
            </form>
          </section>

          <section className="input-field-showcase__matrix" style={matrixStyle}>
            <div className="input-field-showcase__matrix-header-sticky">
              <div
                ref={headerScrollRef}
                className="input-field-showcase__matrix-header-scroll"
                onScroll={handleHeaderScroll}
              >
                <div className="input-field-showcase__matrix-header">
                  {INPUT_FIELD_VARIANTS.map((typeVariant) => (
                    <span
                      key={typeVariant}
                      className="input-field-showcase__matrix-header-cell"
                    >
                      {INPUT_FIELD_VARIANT_LABELS[typeVariant]}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div
              ref={bodyScrollRef}
              className="input-field-showcase__matrix-body-scroll"
              onScroll={handleBodyScroll}
            >
              <div
                ref={bodyContentRef}
                className="input-field-showcase__matrix-body-content"
              >
                {INPUT_FIELD_PREVIEW_STATES.map((previewState) => (
                  <div
                    key={previewState}
                    className="input-field-showcase__matrix-section"
                  >
                    <h4 className="input-field-showcase__matrix-state-label">
                      {INPUT_FIELD_PREVIEW_STATE_LABELS[previewState]}
                    </h4>
                    <div className="input-field-showcase__matrix-row">
                      {INPUT_FIELD_VARIANTS.map((typeVariant) => (
                        <div
                          key={`${previewState}-${typeVariant}`}
                          className="input-field-showcase__matrix-cell"
                        >
                          <InputField
                            variant={typeVariant}
                            previewState={previewState}
                            rows={typeVariant === 'message' ? 3 : undefined}
                            aria-label={`${INPUT_FIELD_VARIANT_LABELS[typeVariant]} ${INPUT_FIELD_PREVIEW_STATE_LABELS[previewState]}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              ref={footerScrollRef}
              className="input-field-showcase__matrix-scroll-sticky"
              onScroll={handleFooterScroll}
              aria-hidden="true"
            >
              <div
                className="input-field-showcase__matrix-scroll-spacer"
                style={{ width: matrixScrollWidth }}
              />
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Input field"
        sections={propertySections}
      />
    </>
  )
}

export default InputFieldShowcase
